
import React from 'react';
import { Rocket, Globe, UserCheck, Battery, Crown, Edit3, Sun, Moon } from 'lucide-react';
import { CompanionAvatar } from './Companion';
import { UserProfile, Language, Theme } from '../types';
import { translations } from '../i18n';

interface HeaderProps {
  user: UserProfile;
  lang: Language;
  theme: Theme;
  onToggleLang: () => void;
  onToggleTheme: () => void;
  onOpenParent: () => void;
  onOpenFocus: () => void;
  onHome: () => void;
  onOpenProfile: () => void; 
  onOpenCompanion: () => void;
  isPro?: boolean;
}

const AVATAR_MAP: Record<string, string> = {
  rocket: '🚀',
  alien: '👽',
  robot: '🤖',
  cat: '🐱',
  dino: '🦖',
  star: '⭐'
};

export const Header: React.FC<HeaderProps> = ({ user, lang, theme, onToggleLang, onToggleTheme, onOpenParent, onOpenFocus, onHome, onOpenProfile, onOpenCompanion, isPro = false }) => {
  const t = translations[lang];
  
  return (
    <header className="sticky top-0 z-40 px-4 md:px-8 py-4 flex justify-between items-center transition-all duration-300">
      {/* Dynamic Glass Background for Header */}
      <div className="absolute inset-0 bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl border-b border-slate-200 dark:border-white/5 shadow-sm dark:shadow-none transition-colors duration-500"></div>
      
      <div className="relative z-10 flex items-center gap-3 cursor-pointer group" onClick={onHome}>
        <div className="bg-gradient-to-br from-indigo-500 to-violet-600 text-white p-2.5 rounded-2xl shadow-lg shadow-indigo-500/30 group-hover:scale-110 transition-transform">
          <Rocket size={20} className="fill-white/20"/>
        </div>
        <div className="hidden sm:block">
          <h1 className="font-black text-slate-800 dark:text-white leading-none tracking-tight text-xl font-orbitron drop-shadow-sm">
            Wonder<span className="text-indigo-500 dark:text-indigo-400">Verse</span>
            {isPro && <Crown className="inline-block ml-1 text-yellow-500 dark:text-yellow-400 mb-1" size={16} />}
          </h1>
          <p className="text-[10px] font-bold text-slate-500 dark:text-indigo-200/60 uppercase tracking-[0.2em]">{t.tagline}</p>
        </div>
      </div>

      <div className="relative z-10 flex items-center gap-2 md:gap-4">
        {/* Theme Toggle */}
        <button 
          onClick={onToggleTheme} 
          className="p-2 rounded-full bg-slate-100 dark:bg-white/10 hover:bg-slate-200 dark:hover:bg-white/20 text-slate-600 dark:text-yellow-300 transition-all active:scale-95"
          title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
        >
          {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
        </button>

        <button onClick={onToggleLang} className="hidden md:flex items-center gap-2 text-xs font-bold bg-slate-100 dark:bg-white/10 hover:bg-slate-200 dark:hover:bg-white/20 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-white px-3 py-1.5 rounded-full backdrop-blur-sm transition-all active:scale-95">
          <Globe size={14}/> {lang === 'en' ? 'EN' : 'ZH'}
        </button>
        
        <button onClick={onOpenParent} className="flex items-center gap-2 text-xs font-bold bg-indigo-50 dark:bg-indigo-500/20 hover:bg-indigo-100 dark:hover:bg-indigo-500/30 border border-indigo-100 dark:border-indigo-500/30 text-indigo-600 dark:text-indigo-100 px-3 py-1.5 rounded-full backdrop-blur-sm transition-all active:scale-95">
          <UserCheck size={14}/> <span className="hidden lg:inline">{t.parents}</span>
        </button>

        <div 
          className="flex items-center gap-2 bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-white/10 text-slate-800 dark:text-white px-4 py-1.5 rounded-full cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-all active:scale-95 shadow-sm dark:shadow-lg"
          onClick={onOpenFocus}
        >
          <Battery size={16} className={user.energy < 20 ? 'text-red-500 animate-pulse' : 'text-yellow-500 fill-yellow-500'} />
          <span className="font-bold text-sm tabular-nums font-orbitron">{user.energy}%</span>
        </div>

        <div className="pl-2 border-l border-slate-200 dark:border-white/10 flex items-center gap-3">
          {/* User Profile Trigger */}
          <div onClick={onOpenProfile} className="flex items-center gap-2 cursor-pointer group">
             <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 border-2 border-white dark:border-indigo-500/50 flex items-center justify-center text-xl shadow-md dark:shadow-[0_0_10px_rgba(99,102,241,0.3)] group-hover:scale-110 transition-transform relative">
                {AVATAR_MAP[user.avatar || 'rocket'] || '🚀'}
                <div className="absolute -bottom-1 -right-1 bg-indigo-600 rounded-full p-0.5 border border-white dark:border-slate-900">
                   <Edit3 size={8} className="text-white"/>
                </div>
             </div>
             <div className="hidden lg:block text-right leading-tight">
                <div className="text-xs font-bold text-slate-700 dark:text-white group-hover:text-indigo-500 dark:group-hover:text-indigo-300 transition-colors">{user.name}</div>
                <div className="text-[10px] text-slate-400">Lv.{user.level}</div>
             </div>
          </div>
          
          {/* Companion Trigger */}
          <div onClick={onOpenCompanion} className="cursor-pointer group relative">
             <div className="absolute inset-0 bg-indigo-400 blur-lg opacity-0 group-hover:opacity-40 transition-opacity rounded-full"></div>
             <CompanionAvatar 
               level={user.level} 
               size="sm" 
               className="hover:scale-110 transition-transform drop-shadow-md ml-2 relative z-10" 
             />
          </div>
        </div>
      </div>
    </header>
  );
};
